import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../../../../api';
import { useLogos } from '../../../../../context/LogoProvider';

// 🔹 Configuración dinámica de URLs basada en API_BASE
const SERVER_URL = API_BASE.replace(/\/api\/?$/, ''); // Elimina /api al final
const UPLOAD_ENDPOINT = `${API_BASE}/upload/certificacion`;

const EMPTY = {
  idCurso: '',
  certificacionUrl: '',
  idFirma: '',
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  cursos = [],
  firmas = [],
  instructores = [],
  empleados = [],
  personas = [],
  onClose,
  onSubmit,
  inline = false,
  loading = false,
}) {
  const { getSystemConfig } = useLogos();
  const [form, setForm] = useState(EMPTY);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previousImagePath, setPreviousImagePath] = useState(null); // Para eliminar la imagen anterior
  const [errors, setErrors] = useState({
    idCurso: '',
    certificacionUrl: '',
    idFirma: '',
  });

  // Helper para obtener nombre de la persona asociada a la firma
  const getPersonaNombreFromFirma = (idFirma) => {
    if (!idFirma) return `Firma --`;
    // 1. Buscar la firma
    const firma = firmas.find(f => Number(f.idFirma) === Number(idFirma));
    if (!firma) return `Firma [${idFirma}] no encontrada`;

    // 2. Buscar el empleado
    const empleado = empleados.find(e => Number(e.idEmpleado) === Number(firma.idEmpleado));
    if (!empleado) return `Firma [${idFirma}] - Empleado no encontrado`;

    // 3. Buscar la persona
    const persona = personas.find(p => Number(p.idPersona) === Number(empleado.idPersona));
    if (!persona) return `Firma [${idFirma}] - Persona no encontrada`;

    // 4. Retornar nombre completo
    const nombre = persona.nombre || '';
    const paterno = persona.apellidoPaterno || '';
    const materno = persona.apellidoMaterno || '';
    return `${nombre} ${paterno} ${materno}`.trim() || `Firma ${idFirma}`;
  };

  // Validación de campo individual
  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'idCurso':
        if (!value || value === '' || value === 0) {
          error = 'Debes seleccionar un curso.';
        }
        break;
      case 'certificacionUrl':
        if (!value) {
          error = 'Debes subir una imagen para la certificación.';
        }
        break;
      case 'idFirma':
        if (!value || value === '' || value === 0) {
          error = 'Debes seleccionar una firma.';
        }
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const fields = Object.keys(form);
    return fields.every((f) => validateField(f, form[f]));
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const newForm = { ...prev, [field]: value };

      if (field === 'idCurso') {
        if (!value) {
          newForm.idFirma = '';
        } else {
          const selectedCurso = cursos.find(c => Number(c.idCurso) === Number(value));
          if (selectedCurso?.idInstructor) {
            const instructor = instructores.find(i => Number(i.idInstructor) === Number(selectedCurso.idInstructor));
            if (instructor) {
              // Buscar firma que no sea de director y pertenezca al empleado del instructor
              const firma = firmas.find(f =>
                Number(f.idEmpleado) === Number(instructor.idEmpleado) &&
                !f.esDirector
              );
              if (firma) {
                newForm.idFirma = firma.idFirma;
                setTimeout(() => validateField('idFirma', firma.idFirma), 0);
              } else {
                newForm.idFirma = '';
              }
            }
          }
        }
      }
      return newForm;
    });
    validateField(field, value);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, certificacionUrl: 'Solo se permiten archivos de imagen.' }));
      return;
    }

    const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
    if (file.size > maxMB * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, certificacionUrl: `La imagen no debe superar los ${maxMB}MB.` }));
      return;
    }

    setSelectedFile(file);
    const blobUrl = URL.createObjectURL(file);
    setForm((prev) => ({ ...prev, certificacionUrlPreview: blobUrl }));

    try {
      setIsUploading(true);
      if (previousImagePath) {
        try {
          await axios.delete(UPLOAD_ENDPOINT, {
            params: { filePath: previousImagePath },
            withCredentials: true
          });
        } catch (deleteError) {
          console.warn('No se pudo eliminar la imagen anterior:', deleteError);
        }
      }

      const formData = new FormData();
      formData.append('file', file);

      const resUpload = await axios.post(UPLOAD_ENDPOINT, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      const resData = resUpload.data;
      setPreviousImagePath(resData.filePath);
      setForm((prev) => ({ ...prev, certificacionUrl: resData.filePath, certificacionUrlPreview: blobUrl }));
      validateField('certificacionUrl', resData.filePath);
    } catch (error) {
      console.error('Error al subir el archivo:', error);
      setErrors((prev) => ({ ...prev, certificacionUrl: error.message || 'Error al subir la imagen al servidor.' }));
      setForm((prev) => ({ ...prev, certificacionUrlPreview: null }));
      setSelectedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = () => {
    const isValid = validateAll();
    if (!isValid) return;
    onSubmit(form, mode);
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const filledForm = {
        idCertificacion: data.idCertificacion ?? 0,
        idCurso: data.idCurso ?? '',
        certificacionUrl: data.certificacionUrl ?? '',
        idFirma: data.idFirma ?? '',
      };

      setForm(filledForm);

      // Guardar la ruta de la imagen actual para poder eliminarla si se actualiza
      if (filledForm.certificacionUrl) {
        setPreviousImagePath(filledForm.certificacionUrl);
      }

      // Validamos todo lo precargado
      for (const key of Object.keys(filledForm)) {
        validateField(key, filledForm[key]);
      }
    } else {
      setForm(EMPTY);
      setPreviousImagePath(null); // Limpiar la imagen anterior en modo nuevo
      setErrors({
        idCurso: '',
        certificacionUrl: '',
        idFirma: '',
      });
    }
  }, [mode, data]);

  if (!open) return null;

  const allRequiredFilled =
    form.idCurso &&
    form.certificacionUrl?.trim() &&
    form.idFirma;

  const hasErrors = Object.values(errors).some((e) => !!e);

  const isSaveDisabled = !allRequiredFilled || hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva certificación' : 'Editar certificación'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      {/* Curso */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Curso:</label>
        <br /><br />
        <select
          className={`infoField${errors.idCurso ? ' infoField--error' : ''}`}
          value={form.idCurso}
          onChange={(e) => handleChange('idCurso', Number(e.target.value) || '')}
        >
          <option key="default-curso" value="">
            {cursos.length === 0 && mode === 'new'
              ? '-- Sin cursos con petición pendiente --'
              : '-- Selecciona un curso --'}
          </option>
          {cursos.map((c, idx) => (
            <option key={c.idCurso || `curso-${idx}`} value={c.idCurso}>{c.nombre}</option>
          ))}
        </select>
        {errors.idCurso && <small style={errorStyle}>{errors.idCurso}</small>}
      </div>

      {/* Firma (mostrando nombre de la persona) */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Instructor:</label>
        <br /><br />
        <div
          className={`infoField${errors.idFirma ? ' infoField--error' : ''}`}
          style={{ 
             padding: '0.6rem 0.8rem', 
             background: '#f8fafc', 
             color: '#334155', 
             display: 'flex', 
             alignItems: 'center', 
             height: '42px', 
             overflow: 'hidden',
             whiteSpace: 'nowrap',
             textOverflow: 'ellipsis'
          }}
        >
          {form.idFirma 
            ? getPersonaNombreFromFirma(form.idFirma) 
            : (form.idCurso ? "Firma del instructor no encontrada" : "-- Selecciona un curso primero --")}
        </div>
        {errors.idFirma && <small style={errorStyle}>{errors.idFirma}</small>}
      </div>

      {/* Imagen de Certificación */}
      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Imagen de Certificación:</label>
        <br /><br />

        <div
          className={`file-uploader${form.certificacionUrl ? ' file-uploader--filled' : ''}${errors.certificacionUrl ? ' file-uploader--error' : ''}${isUploading ? ' file-uploader--uploading' : ''}`}
        >
          {form.certificacionUrl && (
            <div className="file-uploader__badge">
              <span>✓</span>
              <span>Archivo cargado</span>
            </div>
          )}

          {form.certificacionUrl && (
            <div className="file-uploader__preview">
              <img
                src={form.certificacionUrlPreview || (form.certificacionUrl?.startsWith('http') ? form.certificacionUrl : `${SERVER_URL}${form.certificacionUrl}`)}
                alt="Vista previa de la certificación"
              />
            </div>
          )}

          <label className="file-uploader__label">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              onClick={(e) => { e.target.value = ''; }}
              style={{ display: 'none' }}
            />
            <div className="file-uploader__icon">
              {form.certificacionUrl ? '🔄' : '🖼️'}
            </div>
            <div className="file-uploader__text">
              {form.certificacionUrl ? 'Cambiar imagen' : 'Haz clic para subir imagen'}
            </div>
            <div className="file-uploader__hint">
              JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)
            </div>
          </label>
        </div>

        {errors.certificacionUrl && (
          <small style={errorStyle}>{errors.certificacionUrl}</small>
        )}
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={handleSubmit}
        disabled={isSaveDisabled || loading || isUploading}
        style={{
          opacity: isSaveDisabled || loading || isUploading ? 0.5 : 1,
          cursor: isSaveDisabled || loading || isUploading ? 'not-allowed' : 'pointer',
        }}
      >
        {isUploading ? 'Subiendo...' : (loading ? 'Guardando...' : (mode === 'new' ? 'Guardar' : 'Actualizar'))}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
